import { createApi } from '@reduxjs/toolkit/query/react';
import { authQuery } from './baseQuery';
import type { UnassignedEmail, GetUnassignedEmailsArgs } from 'src/types/email.types';
import type { TableResponse } from 'src/types/common';
import { toUtcEndOfDayIso, toUtcStartOfDayIso } from 'src/utils/date-picker';

type OpenEmailResponse = {
  message: string;
  email?: UnassignedEmail;
};

type SendManualEmailResponse = {
  to: string;
  from: string;
  subject: string;
  type: string;
  distributionChannel: string;
};

export const emailsAPISlice = createApi({
  reducerPath: 'presentation-service-emails-api',
  tagTypes: ['Emails', 'UnassignedEmails'],
  baseQuery: authQuery,
  endpoints: (builder) => ({
    // Existing endpoint
    matchEmailToInvoice: builder.mutation({
      query: (requestData: {
        emailId: string;
        invoiceId: string;
        matchConfidence: number;
        matchedBy: string;
      }) => ({
        url: '/emails/matchEmailToInvoice',
        method: 'POST',
        body: requestData,
      }),
    }),

    getUnassignedEmails: builder.query<TableResponse<UnassignedEmail>, GetUnassignedEmailsArgs>({
      query: ({
        page,
        limit,
        search,
        startDate,
        endDate,
        showArchived,
        sortBy = 'receivedAt',
        sortOrder = 'desc',
      }: GetUnassignedEmailsArgs) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sort: sortBy,
          order: sortOrder,
        });

        if (search) {
          params.append('searchTerm', search);
        }
        if (startDate) {
          params.append('createdAtFrom', toUtcStartOfDayIso(startDate) ?? startDate);
        }
        if (endDate) {
          params.append('createdAtTo', toUtcEndOfDayIso(endDate) ?? endDate);
        }
        if (showArchived) {
          params.append('archivedAt', 'not_null');
        }

        return {
          url: `/emails/unassigned?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: any) => ({
        rows: response.data || [],
        totalItems: response.totalItems,
        page: response.page || 1,
        limit: response.limit || 10,
        totalPages: response.totalPages || 1,
      }),
      providesTags: ['UnassignedEmails'],
    }),

    archiveEmails: builder.mutation<
      { archivedCount: number; message: string },
      { ids: string[]; reason?: string }
    >({
      query: ({ ids, reason }) => ({
        url: `/emails/unassigned/archive`,
        method: 'POST',
        body: { ids, reason },
      }),
      invalidatesTags: ['UnassignedEmails'],
    }),

    unarchiveEmail: builder.mutation<any, string>({
      query: (id) => ({
        url: `/emails/unassigned/${id}/unarchive`,
        method: 'POST',
      }),
      invalidatesTags: ['UnassignedEmails'],
    }),

    deleteEmail: builder.mutation<void, string>({
      query: (id) => ({
        url: `/emails/unassigned/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UnassignedEmails'],
    }),

    getEmailAttachments: builder.query<
      Array<{
        id: string;
        filename: string;
        mimeType: string;
        size: number;
        filePath: string;
        uploadedAt: string;
      }>,
      string
    >({
      query: (emailId) => ({
        url: `/emails/${emailId}/attachments`,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        // Handle null/undefined responses
        if (response === null || response === undefined) {
          return [];
        }

        // If response is already an array, return it
        if (Array.isArray(response)) {
          return response;
        }

        // Handle the generated API structure which wraps in { result: [...] }
        if (response && response.result && Array.isArray(response.result)) {
          return response.result;
        }

        // Handle other object responses
        if (response && response.data && Array.isArray(response.data)) {
          return response.data;
        }

        return [];
      },
    }),

    downloadMultipleFiles: builder.mutation<Blob, { fileName: string; filePath?: string }[]>({
      query: (files) => ({
        url: `/files/download-multiple`,
        method: 'POST',
        body: files,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    openEmail: builder.mutation<
      OpenEmailResponse,
      { emailId: string; operatorId: string; listArgs: GetUnassignedEmailsArgs }
    >({
      query: ({ emailId, operatorId }) => ({
        url: `/emails/${emailId}/open`,
        method: 'POST',
        body: { operatorId },
      }),
      async onQueryStarted({ emailId, operatorId, listArgs }, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled;

          dispatch(
            emailsAPISlice.util.updateQueryData('getUnassignedEmails', listArgs, (draftState) => {
              const emailIndex = draftState.rows.findIndex((email) => email.id === emailId);

              const lockedEmailData = response.data?.email;
              if (emailIndex !== -1 && lockedEmailData) {
                draftState.rows[emailIndex] = lockedEmailData;
              }
            })
          );
        } catch (error) {
          console.error('Error opening email', emailId, error);
        }
      },
    }),

    closeEmail: builder.mutation<
      void,
      {
        emailId: string;
        operatorId: string;
        listArgs?: GetUnassignedEmailsArgs;
      }
    >({
      query: ({ emailId, operatorId }) => ({
        url: `/emails/${emailId}/close`,
        method: 'POST',
        body: { operatorId },
      }),
      async onQueryStarted({ emailId, operatorId, listArgs }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          if (listArgs === undefined) {
            return;
          }

          dispatch(
            emailsAPISlice.util.updateQueryData('getUnassignedEmails', listArgs, (draftState) => {
              const emailIndex = draftState.rows.findIndex((email) => email.id === emailId);

              if (emailIndex !== -1) {
                draftState.rows[emailIndex] = {
                  ...draftState.rows[emailIndex],
                  lastLockedTimestamp: null,
                  lastLockedOperatorId: null,
                };
              }
            })
          );
        } catch (error) {
          console.error('Error closing email', emailId, error);
        }
      },
    }),

    //
    // Manually sending an email
    //
    sendManualEmail: builder.mutation<SendManualEmailResponse, FormData>({
      query: (body) => ({
        url: `/emails/out`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useMatchEmailToInvoiceMutation,
  useGetUnassignedEmailsQuery,
  useArchiveEmailsMutation,
  useUnarchiveEmailMutation,
  useDeleteEmailMutation,
  useGetEmailAttachmentsQuery,
  useDownloadMultipleFilesMutation,
  useOpenEmailMutation,
  useCloseEmailMutation,
  useSendManualEmailMutation,
} = emailsAPISlice;
